<template>
  <div class="remove-watch">
    <template v-if="notValid">
      <div class="remove-watch__top">
        <oie-use-svg svg="circle-warning"></oie-use-svg>
        <h1 class="remove-watch__title">
          {{ t('errorTitle') }}
        </h1>
      </div>
    </template>

    <template v-else-if="!success">
      <h1 class="remove-watch__title">
        {{ t('removeWatchTitle') }}
      </h1>
      <p class="remove-watch__description">
        {{ t('removeWatchDescription') }}
      </p>
      <div class="remove-watch__article">
        <template v-if="articleLoaded">
          <a :href="article.items[0].url" class="article__link">
            <oie-base-image
              :images="article.imageUrl ? `${getAssetPath(article.imageUrl)}?preset=ProductSmall&watermark=OIEWatermarkMedium` : `${getAssetPath('/globalassets/missing404/404.png')}?preset=ProductSmall`"
              :alt-text="article.name"
              image-width="82px"
              image-height="82px"
              class="article__image"></oie-base-image>
          </a>

          <div class="article__info">
            <h5 class="article__name">
              <a :href="article.items[0].url" class="article__link">
                {{ article.name }}
              </a>
            </h5>
            <span class="article__article-number">
              {{ tStatic('articleNumberLabel') }} {{ article.items[0].id }}
            </span>
          </div>

          <oie-product-status
            :status="stock.status"
            :text="stock.message">
          </oie-product-status>

          <oie-product-price :current-object="article" :is-product="true"></oie-product-price>
        </template>
      </div>
      <div class="remove-watch__actions">
        <oie-base-button :text="t('confirmButtonLabel')" css-classes="button--red" @click="removeArticleWatch"></oie-base-button>
        <oie-base-button
          :text="t('cancelButtonLabel')"
          is-type="a"
          :url="clientModel.startPageUrl"
          css-classes="remove-watch__actions-cancel"></oie-base-button>
      </div>
    </template>

    <template v-else>
      <div class="remove-watch__top">
        <oie-use-svg svg="circle-check"></oie-use-svg>
        <h1 class="remove-watch__title">
          {{ t('successTitle') }}
        </h1>
      </div>
      <p class="remove-watch__description">
        {{ t('successMessage') }}
      </p>
      <oie-base-button
        :text="t('successButtonLabel')"
        is-type="a"
        :url="clientModel.startPageUrl"
        css-classes="button--blue remove-watch__success-button"></oie-base-button>
    </template>
  </div>
</template>

<script>
import priceService from '@/core/price-service'
import httpService from '@/core/http-service'
import _ from 'lodash'

export default {
  props: {
    guid: {
      type: String,
      required: true
    }
  },
  data: () => ({
    notValid: false,
    article: {},
    articleLoaded: false,
    articleNumber: '',
    stock: {
      status: '',
      message: ''
    },
    success: false
  }),
  mounted() {
    if (this.guid === '') {
      this.notValid = true
      return
    }

    httpService.get('article-watch/by-id', { id: this.guid }).then(res => {
      if (res.data.articleNumber === null) {
        this.notValid = true
        return
      }
      this.articleNumber = res.data.articleNumber
      this.getProduct()
    }, () => { this.notValid = true })
  },
  methods: {
    getProduct() {
      httpService.post('products', { productIds: [ this.articleNumber ], page: 1, pageSize: 1 }).then(result => {
        this.getProductListPrice(result.data.data[0])
      }, () => { this.notValid = true })
    },
    getProductListPrice(product) {
      httpService.post('productListPrices', { productId: [ product.id ] }).then(res => {
        const products = [ product ]
        priceService.populatePricesFromBackend(res.data, products)

        const items = products[0].items
        _.forEach(items, item => {
          if (item.id === this.articleNumber) {
            products[0].items = [item]
            return false
          }
        })

        this.article = products[0]
        this.articleLoaded = true
        this.setStock()
      })
    },
    setStock() {
      var number = this.article.items[0].stock
      var threshold = this.getProductFewInStock()

      if (number === undefined) { number = 0 }

      if (threshold === undefined) { threshold = 0 }

      if (number >= threshold) {
        this.stock.status = 'in-stock'
        this.stock.message = this.tStatic('stockAvailableLabel')
      } else if (number > 0) {
        this.stock.status = 'few-stock'
        this.stock.message = this.tStatic('stockFewLabel')
      } else if (this.article.isOrderItem) {
        this.stock.status = 'is-order-item'
        this.stock.message = this.tStatic('stockIsOrderItemLabel')
      } else {
        this.stock.status = 'no-stock'
        this.stock.message = this.tStatic('stockNoneLabel')
      }
    },
    removeArticleWatch() {
      httpService.delete('article-watch/delete', { id: this.guid }).then(() => {
        this.success = true
      })
    }
  }
}
</script>

<style lang="scss">
  .remove-watch {
    margin: 5rem auto;
    background-color: #fff;
    width: 100%;
    max-width: 900px;
    padding: 2.5rem 5rem 1.5rem 5rem;
  }

  .remove-watch__title {
    font-size: 1.5rem;
    line-height: 33px;
    font-style: normal;
    color: color(gray-1);
    margin-top: 0;
  }

  .remove-watch__description {
    font-size: 0.875rem;
    line-height: 21px;
    margin-bottom: 1.5rem;
  }

  .remove-watch__article {
    padding: 1.25rem 1.5rem;
    border: 1px solid color(gray-4);
    margin-bottom: 3rem;
    display: flex;
    gap: 2rem;
    justify-content: space-between;
    flex-wrap: wrap;

    .article__link {
      display: block;
      color: color(text);
      text-decoration: none;

      &:hover {
        text-decoration: underline;
      }
    }

    .article__info {
      .article__name {
        @include body-bold;
        margin: 0;
      }

      .article__article-number {
        @include caption;
      }
    }
  }

  .remove-watch__actions {
    width: 100%;
    display: flex;
    gap: 1rem;
    flex-direction: row-reverse;
  }

  .remove-watch__actions-cancel {
    background-color: transparent;
  }

  .remove-watch__top {
    display: flex;
    align-items: center;
    gap: 1rem;
    margin-bottom: 1rem;

    .remove-watch__title {
      margin: 0;
    }
  }

  .remove-watch__success-button {
    display: inline-flex;
  }
</style>
